import React, { useCallback, useState } from 'react';
import { Layout, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useLocation } from 'react-router-dom';
import { TTBFraudPageHeader } from '../../organisms/ttb-fraud/TTBFraudPageHeader';
import { TTBFraudTable } from '../../organisms/ttb-fraud/TTBFraudTable';
import { FilterTTBFraudType, SortTTBFraudType, TTBFraudType } from '../../../types/tryThenBuyFraud';
import { getTTBFraudListRepository } from '../../../repository/mytm/try-then-buy-fraud/ttbFraudRepository';
import { PAGE_SIZE_OPTIONS, PER_PAGE_DEFAULT } from '../../../constants/common';
import { API_URLS } from '../../../constants/urls/api-urls';
import { TTBFraudFilter } from '../../organisms/ttb-fraud/TTBFraudFilter';
import { TTBFraudThreshold } from '../../organisms/ttb-fraud/TTBFraudThreshold';
import useDebounce from '../../../hooks/debounced';
import { getProductImageUrlFromSelectNodes, getProductNameFromHeadModel } from '../../../utils/transfer/ttb-fraud';
import { appStyles } from '../../../styles';
import { TRY_THEN_BUY_FRAUD } from '../../../constants/define/translation';

const { Content } = Layout;

export const TryThenBuyFraud = () => {
  const { search } = useLocation();
  const orderId: string = new URLSearchParams(search).get('orderId') || '';
  const filterDefault = {
    strSearch: orderId,
    status: [],
  };
  const sortDefault = {
    sortBy: null,
    sortType: null,
  };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_DEFAULT);
  const [loading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<TTBFraudType[]>([]);
  const [filterData, setFilterData] = useState<FilterTTBFraudType>(filterDefault);
  const [sortData, setSortData] = useState<SortTTBFraudType>(sortDefault);
  const debouncedSearch = useDebounce(filterData.strSearch, 500);
  const { t } = useTranslation(TRY_THEN_BUY_FRAUD);

  const handleChangePage = useCallback((pg: number, pgSize?: number) => {
    setPage(pg);
    setPageSize(pgSize || PER_PAGE_DEFAULT);
  }, []);

  const onShowSizeChange = useCallback((current: number, size: number) => {
    setPage(current);
    setPageSize(size || PER_PAGE_DEFAULT);
  }, []);

  const { isValidating, error, mutate } = useSWR(
    [
      API_URLS.TRY_THEN_BUY_FRAUD_LIST,
      page,
      pageSize,
      filterData.status,
      sortData.sortBy,
      sortData.sortType,
      debouncedSearch,
    ],
    (
      url: string,
      pageNumber: number,
      pageSizeNumber: number,
      status: any,
      sortBy: string,
      sortType: string,
      strSearch: any,
    ) =>
      getTTBFraudListRepository(url, {
        status: status.join(','),
        page: pageNumber,
        take: pageSizeNumber || PER_PAGE_DEFAULT,
        strSearch: strSearch.trim(),
        sortBy,
        sortType,
      }).then(dataOrder => {
        if (dataOrder?.total === 0 && debouncedSearch) {
          message.destroy();
          message.error(t('message.orderNotFound', { orderId: debouncedSearch }));
        }
        setTotal(dataOrder.total);
        const recordTTBFraud = dataOrder.orders.map((item: TTBFraudType, index: number): any => ({
          ...item,
          index,
          key: index,
          price: item?.productInfo?.price,
          productType: item?.productInfo?.type,
          productId: item?.productInfo?.productId,
          productName: getProductNameFromHeadModel(item?.productInfo?.cTmCustomconfiguratorNodes || {}),
          productImageUrl: getProductImageUrlFromSelectNodes(item?.productInfo?.cTmCustomconfiguratorNodes || {}),
        }));
        setData(recordTTBFraud || []);
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const getFilteredData = useCallback((filterValue: FilterTTBFraudType) => {
    setFilterData(old => ({ ...old, ...filterValue }));
    setPage(1);
    setPageSize(PER_PAGE_DEFAULT);
  }, []);

  const onChangeSort = useCallback(sorter => {
    setSortData(old => {
      if (old.sortBy !== sorter.sortBy || old.sortType !== sorter.sortType) {
        setPage(1);
      }
      return sorter;
    });
  }, []);

  if (error) {
    message.error(error.message);
  }

  return (
    <>
      <Container>
        <Content>
          <TTBFraudPageHeader />
          <TTBFraudFilter options={filterData} getFilteredData={getFilteredData} />
          <TTBFraudThreshold />
          <TTBFraudTable
            onRefresh={mutate}
            records={data}
            loading={loading || isValidating}
            onChangeSort={onChangeSort}
            pagination={{
              showSizeChanger: true,
              onChange: handleChangePage,
              pageSize,
              current: page,
              total,
              onShowSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
            isSearching={debouncedSearch !== ''}
          />
        </Content>
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }
`;
